import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LoadingOverlay from '../Components/LoadingOverlay';
import CustomAlert from '../Components/CustomAlert';
import QualificationService from '../Api/Routes/Qualification/Qualification';
import '../Assets/Styles/PreScreeningPage.css';
import { useLanguage } from '../Hooks/useLanguage';
import getGeoIp from '../Middleware/GeoIp/getGeoIp';
import CryptoJS from 'crypto-js';
import { FaDesktop, FaMobileAlt, FaTabletAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {GeoIpResult, RootState} from '../Types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Cookies from 'js-cookie';
import { authSuccess } from '../Store/slices/auth.slice';
import { userProfileLoaded } from '../Store/slices/user.slice';

const baseUrl = process.env.REACT_APP_API_DEV_URL || 'https://backend.afrisight.com/api/v1';

interface Translation {
    language: string;
    translatedText?: string;
    label?: string;
}

interface AnswerOption {
    label: string;
    option: string;
    translations?: Translation[];
}

interface ScreeningQuestion {
    questionId: number;
    question: string;
    questionType: 'single_choice' | 'multiple_choice' | 'open_text';
    answerOptions: AnswerOption[];
    translations?: Translation[];
}



interface Alert {
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
}

enum Status {
    SELECTED = 'SELECTED',
    RESELECTED = 'RESELECTED',
    INVITED = 'INVITED',
    REMINDED = 'REMINDED',
    ENROLLING = 'ENROLLING',
    TARGET_SUITABLE = 'TARGET_SUITABLE',
    TARGET_UNSUITABLE = 'TARGET_UNSUITABLE',
    STARTED = 'STARTED',
    ABANDONED = 'ABANDONED',
    COMPLETED = 'COMPLETED',
    DISQUALIFIED = 'DISQUALIFIED',
    SCREEN_OUT = 'SCREEN_OUT',
    POST_DISQUALIFIED = 'POST_DISQUALIFIED',
    QUOTA_FULL = 'QUOTA_FULL',
    CLOSED = 'CLOSED',
    BOT_DETECTED = 'BOT_DETECTED',
    GEO_LOCKED = 'GEO_LOCKED'
}

const detectDeviceType = (): 'Mobile' | 'Tablet' | 'Desktop' => {
    const userAgent = navigator.userAgent;
    if (/Mobi|Android/i.test(userAgent)) {
        return 'Mobile';
    }
    if (/Tablet|iPad/i.test(userAgent)) {
        return 'Tablet';
    }
    return 'Desktop';
};

const PreScreening: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { language, setLanguage } = useLanguage();
    const { t } = useTranslation();
    const HOME = `/${language}/home`;
    const [questions, setQuestions] = useState<ScreeningQuestion[]>([]);
    const [responses, setResponses] = useState<{ [key: number]: string | string[] }>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [alert, setAlert] = useState<Alert | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [honeypot, setHoneypot] = useState<string>('');
    const [geoIpCountry, setGeoIpCountry] = useState<string>('');
    const [redirecting, setRedirecting] = useState<boolean>(false);
    const [deviceRestricted, setDeviceRestricted] = useState<boolean>(false);
    const [copySuccess, setCopySuccess] = useState<string | null>(null);
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const loggedInGeoIpCountry = useSelector((state: RootState) => state?.country?.countryCode);

    const qualificationService = new QualificationService();


    const extractEncryptedData = useCallback((): string | null => {
        const queryParams = new URLSearchParams(location.search);
        return queryParams.get('data');
    }, [location.search]);

    const preserveUrlWithEncryptedData = (newLanguage: string) => {
        const encryptedData = extractEncryptedData();
        if (encryptedData) {
            navigate(`/${newLanguage}/enrollment/secure?data=${encodeURIComponent(encryptedData)}`);
        }
    };


    const redirectToExternalSite = (url: string) => {
        setRedirecting(true);
        setTimeout((): void => {
            window.location.href = url;
        }, 1000);
    };

    const validateGeoIpLocation = async (countryCode: string): Promise<boolean> => {
        if (loggedInGeoIpCountry) {
            return loggedInGeoIpCountry.toLowerCase() === countryCode?.toLowerCase();
        } else {
            try {
                const geoIpDetails: GeoIpResult = await getGeoIp();
                if (geoIpDetails?.data?.country_code) {
                    const resolvedCountryCode = geoIpDetails.data.country_code.toLowerCase();
                    setGeoIpCountry(resolvedCountryCode);
                    return resolvedCountryCode === countryCode?.toLowerCase();
                }
            } catch (error) {
                return false;
            }
        }
        return false;
    };



    const validateDeviceCompatibility = (deviceRestrictions: string[]): boolean => {
        const deviceType = detectDeviceType();
        if (deviceRestrictions.includes(deviceType.toLowerCase())) {
            setDeviceRestricted(true);
            return false;
        }
        return true;
    };

    const redirectToResultPage = (projectCode: string, status: Status) => {
        const encryptedStatus = CryptoJS.AES.encrypt(
            JSON.stringify({ projectCode, status }),
            process.env.REACT_APP_CRYPTO_KEY || 'default_key'
        ).toString();
        navigate(`/${language}/survey/result?data=${encodeURIComponent(encryptedStatus)}`);
    };

    const autoLoginAndInitiateScreening = async (personId: number): Promise<boolean> => {
        try {
            const response = await axios.post(`${baseUrl}/user/auto-login`, { personId });
            if (response.data) {
                Cookies.set('jwt', response.data.data.jwt, { expires: 1, secure: true, sameSite: 'Strict' });
                Cookies.set('accessToken', response.data.data.accessToken, { expires: 1, secure: true, sameSite: 'Strict' });
                Cookies.set('refreshToken', response.data.data.refreshToken, { expires: 7, secure: true, sameSite: 'Strict' });

                dispatch(authSuccess({
                    user: response.data.data.user,
                    jwt: response.data.data.jwt,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken,
                    mfaRequired: response.data.data.mfaRequired,
                }));

                dispatch(
                    userProfileLoaded({ email: response?.data?.data?.user?.email, role: response?.data?.data?.user?.role })
                );
                await new Promise((resolve) => setTimeout(resolve, 2000));
                return true;
            }
        } catch {
            return false;
        }
        return false;
    };

    const initiateScreeningProcess = useCallback(
        async (projectCode: string, countryCode: string, deviceRestrictions: string[], testFlag: boolean) => {
            try {
                if (!testFlag) {
                    const isGeoIpValid = await validateGeoIpLocation(countryCode);
                    if (!isGeoIpValid) {
                        redirectToResultPage(projectCode, Status.GEO_LOCKED);
                        return;
                    }

                    const isDeviceCompatible = validateDeviceCompatibility(deviceRestrictions);
                    if (!isDeviceCompatible) {
                        redirectToResultPage(projectCode, Status.TARGET_UNSUITABLE);
                        return;
                    }
                }

                const data = await qualificationService.initiateScreening(projectCode);

                if (!data.data.questions || data.data.questions.length === 0) {
                    const encryptedStartStatus = CryptoJS.AES.encrypt(
                        JSON.stringify({ projectCode, status: Status.STARTED }),
                        process.env.REACT_APP_CRYPTO_KEY || 'default_key'
                    ).toString();
                    await qualificationService.updateRespondentStatus(encryptedStartStatus);
                    redirectToExternalSite(data.data.surveyLink);
                } else {
                    setQuestions(data.data.questions);
                }
            } catch(error: any) {
                setAlert({ type: 'error', message: error.message || t('preScreening.error_message') });
                setTimeout(() => {
                    navigate(HOME, { replace: true });
                }, 5000);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        const startProcess = async () => {
            setLoading(true);
            try {
                const encryptedData = extractEncryptedData();
                if (!encryptedData) throw new Error(t('preScreening.error_message'));

                const bytes = CryptoJS.AES.decrypt(encryptedData, process.env.REACT_APP_CRYPTO_KEY || 'default_key');
                const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
                if (!decryptedData) throw new Error(t('preScreening.error_message'));

                const { projectCode, countryCode, deviceRestrictions, personId, language: decryptedLanguage, isTest } = decryptedData;

                const testFlag = isTest || false;

                if (decryptedLanguage) {
                    setLanguage(decryptedLanguage.toLowerCase());
                    preserveUrlWithEncryptedData(decryptedLanguage.toLowerCase());
                }

                if (!isAuthenticated && personId) {
                    const autoLoginSuccessful = await autoLoginAndInitiateScreening(personId);
                    if (autoLoginSuccessful) {
                        initiateScreeningProcess(projectCode, countryCode, deviceRestrictions, testFlag);
                    }
                } else {
                    initiateScreeningProcess(projectCode, countryCode, deviceRestrictions, testFlag);
                }
            } catch(error: any) {
                setAlert({ type: 'error', message: error.message || t('preScreening.error_message') });
                setTimeout(() => {
                    navigate(HOME, { replace: true });
                }, 3000);
            }
        };

        startProcess();
    }, []);

    const handleOptionChange = (questionId: number, option: string) => {
        setResponses((prev) => ({ ...prev, [questionId]: option }));
    };

    const handleMultiOptionChange = (questionId: number, option: string) => {
        setResponses((prev) => {
            const currentSelection = (prev[questionId] as string[]) || [];
            return currentSelection.includes(option)
                ? { ...prev, [questionId]: currentSelection.filter((id) => id !== option) }
                : { ...prev, [questionId]: [...currentSelection, option] };
        });
    };

    const handleTextChange = (questionId: number, value: string) => {
        setResponses((prev) => ({ ...prev, [questionId]: value }));
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setCopySuccess(t('preScreening.copy_link_success'));
            setTimeout(() => setCopySuccess(null), 3000);
        }).catch(() => {
            setCopySuccess(t('preScreening.copy_link_failure'));
            setTimeout(() => setCopySuccess(null), 3000);
        });
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const encryptedData = extractEncryptedData();
        const bytes = CryptoJS.AES.decrypt(encryptedData!, process.env.REACT_APP_CRYPTO_KEY || 'default_key');
        const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        const { projectCode } = decryptedData;

        if (honeypot) {
            redirectToResultPage(projectCode, Status.BOT_DETECTED);
            return;
        }

        const incompleteAnswers = questions.some((question) => {
            const response = responses[question.questionId];
            if (question.questionType === 'single_choice' || question.questionType === 'open_text') {
                return !response;
            }
            if (question.questionType === 'multiple_choice') {
                return (response as string[]).length === 0;
            }
            return false;
        });

        if (incompleteAnswers) {
            setAlert({ type: 'error', message: t('preScreening.all_questions_mandatory') });
            return;
        }

        setLoading(true);
        try {
            const response = await qualificationService.responentScreeningFinisher(
                projectCode,
                Object.keys(responses).map((key) => ({
                    questionId: Number(key),
                    answer: Array.isArray(responses[Number(key)])
                        ? (responses[Number(key)] as string[]).join(',')
                        : (responses[Number(key)] as string),
                }))
            );

            if (response.data.status === Status.TARGET_SUITABLE) {
                const encryptedStartStatus = CryptoJS.AES.encrypt(
                    JSON.stringify({ projectCode, status: Status.STARTED }),
                    process.env.REACT_APP_CRYPTO_KEY || 'default_key'
                ).toString();
                await qualificationService.updateRespondentStatus(encryptedStartStatus);
                redirectToExternalSite(response.data.surveyLink);
            } else {
                const status = response.data.status.toUpperCase() as Status;
                redirectToResultPage(projectCode, status);
            }
        } catch (error: any) {
            if (error.response?.status === 403) {
                redirectToResultPage(projectCode, Status.TARGET_UNSUITABLE);
            } else if (error.response?.status === 400 && error.response.data.message === 'QUOTA_FULL') {
                redirectToResultPage(projectCode, Status.QUOTA_FULL);
            } else {
                setError(error.message || t('preScreening.error_submitting'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmCancel = () => {
        setResponses({});
        setShowConfirmModal(false);
        navigate(HOME);
    };

    const supportedDevices = (['Mobile', 'Tablet', 'Desktop'] as Array<'Mobile' | 'Tablet' | 'Desktop'>).filter(
        (device) => !deviceRestricted
    );

    if (loading || redirecting) {
        return <LoadingOverlay />;
    }

    if (deviceRestricted) {
        return (
            <div className="pre-screening">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>{t('preScreening.device_restriction_title')} - AfriSight</title>
                </Helmet>
                <div className="pre-screening__content">
                    <h1 className="pre-screening__heading">{t('preScreening.device_restriction_title')}</h1>
                    <p className="pre-screening__paragraph">{t('preScreening.device_restriction_paragraph')}</p>
                    <div className="pre-screening__link-box">
                        <input type="text" readOnly value={window.location.href} className="pre-screening__link-input" />
                        <button onClick={handleCopy} className="pre-screening__copy-button">
                            {t('preScreening.copy_link_button')}
                        </button>
                    </div>
                    {copySuccess && <p className="pre-screening__copy-success">{copySuccess}</p>}
                    <div className="pre-screening__supported-devices">
                        <h2 className="pre-screening__supported-devices-heading">{t('preScreening.supported_devices_heading')}</h2>
                        <ul className="pre-screening__supported-devices-list">
                            {supportedDevices.map((device) => (
                                <li key={device} className="pre-screening__supported-device">
                                    {device === 'Mobile' && <FaMobileAlt />}
                                    {device === 'Tablet' && <FaTabletAlt />}
                                    {device === 'Desktop' && <FaDesktop />}
                                    <span className="pre-screening__device-text">{device}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pre-screening">
            <Helmet>
                <meta charSet="utf-8" />
                <title>{t('preScreening.enrollment_title')} - AfriSight</title>
            </Helmet>
            <div className="pre-screening__content">
                {alert && <CustomAlert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
                {!error ? (
                    <div>
                        <h1 className="pre-screening__heading">{t('preScreening.prescreening_heading')}</h1>
                        <p className="pre-screening__paragraph">{t('preScreening.prescreening_paragraph')}</p>
                    </div>
                ) : (
                    <p className="pre-screening__error-message">{error}</p>
                )}
                {!error && questions.length > 0 ? (
                    <form onSubmit={handleSubmit}>
                        <input
                            type="text"
                            name="honeypot"
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                            className="pre-screening__honeypot"
                        />
                        {questions.map((question) => (
                            <div key={question.questionId} className="pre-screening__question">
                                <h3>
                                    {
                                        question.translations?.find((translation) => translation.language === language)?.translatedText
                                        || question.question
                                    }
                                </h3>
                                {question.questionType === 'single_choice' &&
                                    question.answerOptions.map((option) => (
                                        <div key={option.option} className="pre-screening__custom-radio">
                                            <input
                                                id={`option-${option.option}`}
                                                type="radio"
                                                name={`question-${question.questionId}`}
                                                value={option.option}
                                                checked={responses[question.questionId] === option.option}
                                                onChange={() => handleOptionChange(question.questionId, option.option)}
                                                className="pre-screening__hidden-input"
                                            />
                                            <label htmlFor={`option-${option.option}`}>
                                                {
                                                    option.translations?.find((translation) => translation.language === language)?.label
                                                    || option.label
                                                }
                                            </label>
                                        </div>
                                    ))
                                }
                                {question.questionType === 'multiple_choice' &&
                                    question.answerOptions.map((option) => (
                                        <div key={option.option} className="pre-screening__custom-checkbox">
                                            <input
                                                id={`option-${option.option}`}
                                                type="checkbox"
                                                name={`question-${question.questionId}`}
                                                value={option.option}
                                                checked={(responses[question.questionId] as string[] || []).includes(option.option)}
                                                onChange={() => handleMultiOptionChange(question.questionId, option.option)}
                                                className="pre-screening__hidden-input"
                                            />
                                            <label htmlFor={`option-${option.option}`}>
                                                {
                                                    option.translations?.find((translation) => translation.language === language)?.label
                                                    || option.label
                                                }
                                            </label>
                                        </div>
                                    ))
                                }
                                {question.questionType === 'open_text' && (
                                    <input
                                        type="text"
                                        name={`question-${question.questionId}`}
                                        value={responses[question.questionId] as string || ''}
                                        onChange={(e) => handleTextChange(question.questionId, e.target.value)}
                                        className="pre-screening__text-input"
                                    />
                                )}
                            </div>
                        ))}
                        <div className="pre-screening__form-buttons">
                            <button type="submit" className="pre-screening__button pre-screening__button--save">
                                {t('preScreening.submit_button')}
                            </button>
                            <button type="button" className="pre-screening__button pre-screening__button--cancel" onClick={handleCancel}>
                                {t('preScreening.cancel_button')}
                            </button>
                        </div>
                    </form>
                ) : (
                    !error && (
                        <div className="pre-screening__no-questions">
                            <p className="pre-screening__paragraph">{t('preScreening.no_questions_paragraph')}</p>
                        </div>
                    )
                )}
            </div>
            {showConfirmModal && (
                <div className="pre-screening__confirm-modal">
                    <div className="pre-screening__confirm-modal-content">
                        <p className="pre-screening__paragraph">{t('preScreening.cancel_modal_message')}</p>
                        <div className="pre-screening__confirm-modal-buttons">
                            <button className="pre-screening__button--confirm" onClick={handleConfirmCancel}>
                                {t('preScreening.confirm_button')}
                            </button>
                            <button className="pre-screening__button--deny" onClick={() => setShowConfirmModal(false)}>
                                {t('preScreening.deny_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreScreening;
